'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UsedOutsideProviderError } from '@diritto/common/utils/exceptions';
import { decodeFromBase64, encodeToBase64 } from '@diritto/common/utils/crypto';
import { HomeSectionViewMode } from 'constants/homeSectionViewMode';
import { getCookie, getCookieOptions, setCookie } from 'utils/cookie';
import { CookieExpireTime, CookieKey } from 'constants/cookie';
import { useAuthState } from 'contexts/AuthProvider';

interface HomeSettingState {
  viewMode: HomeSectionViewMode;
  changeViewMode: (viewMode: HomeSectionViewMode) => void;
  isHomeTextModeEnabled: boolean;
  isTextMode: boolean;
  categoryIds: number[];
  changeCategoryIds: (categoryIds: number[]) => void;
}

const HomeSettingContext = createContext<HomeSettingState | undefined>(undefined);

interface HomeSettingProviderProps {
  children: ReactNode;
  viewMode?: HomeSectionViewMode;
  isHomeTextModeEnabled?: boolean;
  categoryIds?: number[];
}

export const HomeSettingProvider = ({
  children,
  viewMode: initialViewMode = HomeSectionViewMode.Default,
  isHomeTextModeEnabled = false,
  categoryIds: initialCategoryIds = [],
}: HomeSettingProviderProps) => {
  const { profile } = useAuthState();
  const [viewMode, setViewMode] = useState<HomeSectionViewMode>(initialViewMode);
  const [categoryIds, setCategoryIds] = useState<number[]>(initialCategoryIds);

  useEffect(() => {
    if (profile?.favoriteCategoryIds) {
      setCategoryIds(profile.favoriteCategoryIds);
    } else {
      setCategoryIds(
        decodeFromBase64<number[]>(getCookie<string>(CookieKey.FavoriteCategoryIds)) ?? initialCategoryIds,
      );
    }
  }, [profile?.favoriteCategoryIds]);

  const changeViewMode = useCallback((newViewMode: HomeSectionViewMode) => {
    setViewMode(newViewMode);
    setCookie(
      CookieKey.HomeSectionViewMode,
      newViewMode,
      getCookieOptions({ maxAge: CookieExpireTime[CookieKey.HomeSectionViewMode] }),
    );
  }, []);

  const changeCategoryIds = useCallback((newCategoryIds: number[]) => {
    const encodedCategoryIds = encodeToBase64<number[]>(newCategoryIds);
    if (!encodedCategoryIds) {
      return;
    }

    setCategoryIds(newCategoryIds);
    setCookie(
      CookieKey.FavoriteCategoryIds,
      encodedCategoryIds,
      getCookieOptions({ maxAge: CookieExpireTime[CookieKey.FavoriteCategoryIds] }),
    );
  }, []);

  const state = useMemo(
    () => ({
      viewMode,
      changeViewMode,
      isHomeTextModeEnabled,
      isTextMode: viewMode === HomeSectionViewMode.Text,
      categoryIds,
      changeCategoryIds,
    }),
    [viewMode, changeViewMode, isHomeTextModeEnabled, categoryIds, changeCategoryIds],
  );

  return <HomeSettingContext.Provider value={state}>{children}</HomeSettingContext.Provider>;
};

export const useHomeSettingState = () => {
  const context = useContext(HomeSettingContext);

  if (context === undefined) {
    throw new UsedOutsideProviderError('useHomeSettingState was used outside of its Provider');
  }

  return context;
};
